import React, { useEffect, useState } from 'react';
import {
  Button,
  Grid,
  Paper,
  TextField,
  CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import api from '../../../api/axios';
import { RootState } from '../../../store';

type UserDetails = {
  firstName: string;
  lastName: string;
  phone: string;
  street1: string;
  street2: string;
  postalCode: string;
  city: string;
  region: string;
  nationality: string;
  iban: string;
};

const EditProfile: React.FC = () => {
  const { t } = useTranslation();
  const userId = useSelector((state: RootState) => state.auth.userId);
  const [formData, setFormData] = useState<UserDetails | null>(null);
  const [initialData, setInitialData] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const res = await api.get(`user/${userId}/fetch-user-details`);
        setFormData(res.data);
        setInitialData(res.data);
      } catch {
        setError(t('profile.edit.errorLoad'));
      } finally {
        setLoading(false);
      }
    };
    fetchUserDetails();
  }, [userId, t]);

  const handleChange = (field: keyof UserDetails, value: string) => {
    setFormData(prev => prev ? { ...prev, [field]: value } : prev);
  };

  const handleSave = async () => {
    if (!formData) return;

    const savePromise = api.put(`user/${userId}/edit-profile`, formData);

    await toast.promise(savePromise, {
      pending: t('toastify.pending'),
      success: t('profile.edit.success'),
      error: t('profile.edit.error')
    });

    // Nach dem Speichern initialData updaten, damit Button wieder disabled ist
    setInitialData(formData);
  };

  const hasChanges =
      JSON.stringify(formData) !== JSON.stringify(initialData);

  if (loading) {
    return (
        <div className="flex justify-center items-center h-full">
          <CircularProgress />
        </div>
    );
  }

  if (error || !formData) {
    return <div className="text-red-500 text-center">{error ?? 'Unknown error'}</div>;
  }

  return (
      <Paper className="p-6 max-w-2xl mx-auto">
        <h1 className="text-2xl font-semibold mb-6">{t('profile.edit.title')}</h1>
        <Grid container spacing={2}>
          {/* Vor- & Nachname */}
          <Grid item xs={6}>
            <TextField
                label={t('profile.edit.firstName')}
                value={formData.firstName}
                onChange={e => handleChange('firstName', e.target.value)}
                fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
                label={t('profile.edit.lastName')}
                value={formData.lastName}
                onChange={e => handleChange('lastName', e.target.value)}
                fullWidth
            />
          </Grid>

          {/* Straße & Hausnummer */}
          <Grid item xs={6}>
            <TextField
                label={t('profile.edit.street')}
                value={formData.street1}
                onChange={e => handleChange('street1', e.target.value)}
                fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
                label={t('profile.edit.street2')}
                value={formData.street2}
                onChange={e => handleChange('street2', e.target.value)}
                fullWidth
            />
          </Grid>

          {/* Stadt & PLZ */}
          <Grid item xs={6}>
            <TextField
                label={t('profile.edit.city')}
                value={formData.city}
                onChange={e => handleChange('city', e.target.value)}
                fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
                label={t('profile.edit.postalCode')}
                value={formData.postalCode}
                onChange={e => handleChange('postalCode', e.target.value)}
                fullWidth
            />
          </Grid>

          {/* Land */}
          <Grid item xs={6}>
            <TextField
                label={t('profile.edit.nationality')}
                value={formData.nationality}
                onChange={e => handleChange('nationality', e.target.value)}
                fullWidth
            />
          </Grid>

          {/* Region */}
          <Grid item xs={6}>
            <TextField
                label={t('profile.edit.region')}
                value={formData.region}
                onChange={e => handleChange('region', e.target.value)}
                fullWidth
            />
          </Grid>

          {/* IBAN */}
          <Grid item xs={6}>
            <TextField
                label={t('profile.edit.iban')}
                value={formData.iban}
                onChange={e => handleChange('iban', e.target.value)}
                fullWidth
            />
          </Grid>

          {/* Telefon */}
          <Grid item xs={6}>
            <TextField
                label={t('profile.edit.phone')}
                value={formData.phone}
                onChange={e => handleChange('phone', e.target.value)}
                fullWidth
            />
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <div className="flex justify-end mt-6 space-x-4">
          <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={!hasChanges}
          >
            {t('profile.edit.save')}
          </Button>
        </div>
      </Paper>
  );
};

export default EditProfile;
